import React, { useState } from "react";
import {
    createTheme,
    IconButton,
    Modal,
    TextField,
    ThemeProvider,
    Button,
    createStyles,
    makeStyles,
} from "@material-ui/core";
import styles from "./EditCaption.module.css";
import EditIcon from "@material-ui/icons/Edit";
import { useDispatch, useSelector } from "react-redux";
import { EditVideoCaption } from "../../../store/actions/ExVideoActions";
import { AppState } from "../../../root/RootReducer";
import PuiTypography from "../../../components/elements/PuiTypography";
import { t } from "i18next";

interface EditCaptionProps {
    captionId: number;
    beforeEditText: string;
    setEditModalOpen: (target: boolean) => void;
    setIsCaptionEditing: (target: boolean) => void;
    isEdited: boolean;
}

const theme = createTheme({
    palette: {
        secondary: {
            main: "#4395EA",
        },
    },
});

const useStyles = makeStyles(() =>
    createStyles({
        title: {
            width: "100%",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontWeight: "bold",
            "& .MuiInputBase-root": {
                height: "40px",
                "& .MuiInputBase-input": {
                    border: "none",
                    color: "white",
                    opacity: "0.3",
                    fontFamily: "Yu Gothic",
                },
            },
        },
        textField: {
            width: "432px",
            marginTop: "8px",
            background: "#FFFFFF0D",
            border: "none",
            borderRadius: "5px",
            overflowY: "auto",
            "& .MuiInputBase-root": {
                minHeight: "160px",
                overflow: "auto",
                "&:hover": {
                    background: "#333A43",
                },
                "& .MuiInputBase-input": {
                    height: "123px !important",
                    color: "white",
                    fontFamily: "Yu Gothic",
                },
            },
        },
    })
);

export default function EditCaption({
    captionId,
    beforeEditText,
    setEditModalOpen,
    setIsCaptionEditing,
    isEdited,
}: EditCaptionProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { user } = useSelector((state: AppState) => ({
        user: state.user.user,
    }));

    // STT編集モーダル表示制御
    const [isOpenEditCaptionModal, ShowEditCaptionModal] = useState(false);
    const [isErrorInputCaption, ErrorInputCaption] = useState(false);
    const [inputCaption, InputCaption] = useState(beforeEditText);
    const [isUserInput, ChangeUserInputFlag] = useState(false);

    const [isShowSubTitle, setShowSubTitle] = useState(false);

    const OpenEditCaptionModal = () => {
        ShowEditCaptionModal(true);
        setIsCaptionEditing(true);
        setEditModalOpen(true);
    };

    const CloseEditCaptionModal = () => {
        ShowEditCaptionModal(false);
        setIsCaptionEditing(false);
        setEditModalOpen(false);
        ErrorInputCaption(false);
    };

    // STT自動翻訳確認モーダル表示制御
    const [isOpenTransitionModal, ShowTranslateModal] = useState(false);

    /* 現状は必要ないが、復活の可能性もあるためコメントアウト
    const OpenTranslateModal = () => {
        ShowTranslateModal(true);
    };
    */

    const CloseTranslateModal = () => {
        ShowTranslateModal(false);
        ChangeUserInputFlag(false);
        InputCaption("");
    };

    const InputEditCaption = (event: any) => {
        ChangeUserInputFlag(true);
        InputCaption(event.target.value);
    };

    //日本語のみ修正
    const DoneEditInputCaption = () => {
        if (inputCaption !== "" && user) {
            CloseEditCaptionModal();
            dispatch(
                EditVideoCaption({
                    captionId: captionId,
                    userId: user.id,
                    beforeUpdateTranscript: beforeEditText,
                    afterUpdateTranscript: inputCaption,
                    language: "ja,"
                })
            );
            //OpenTranslateModal();
            //画面リフレッシュのためには導線を変える必要があるため、いったんリロード処理を行う
            window.location.reload();
        } else {
            ErrorInputCaption(true);
        }
    };

    //多言語自動翻訳
    const DoneTranslateCaption = () => {
        if (user) {
            CloseTranslateModal();
            dispatch(
                EditVideoCaption({
                    captionId: captionId,
                    userId: user.id,
                    beforeUpdateTranscript: beforeEditText,
                    afterUpdateTranscript: inputCaption,
                    language: "ja,en,zh-Hans,vi,"
                })
            );
        } else {
            ErrorInputCaption(true);
        }
    };

    return (
        <div>
            <IconButton onClick={OpenEditCaptionModal} className={styles.captionEditIcon}>
                <EditIcon
                    style={{ width: "17px", height: "17px", color: "var(--button-bg)" }}
                    color="secondary"
                    onMouseEnter={() => setShowSubTitle(true)}
                    onMouseLeave={() => setShowSubTitle(false)}
                />
                {isShowSubTitle && (
                    <div className={styles.subTitle}>
                        <PuiTypography color="black" size={12}>
                            {t("編集")}
                        </PuiTypography>
                    </div>
                )}
            </IconButton>

            {/* STT編集モーダル */}
            <Modal open={isOpenEditCaptionModal} onClose={CloseEditCaptionModal}>
                <div className={styles.captionEditModal}>
                    <div style={{ display: "flex" }}>
                        <div className={styles.modalLeft}>
                            <PuiTypography size={14} color="gray-85">
                                {t("編集前")}
                            </PuiTypography>
                            <TextField
                                disabled
                                autoComplete="off"
                                variant="outlined"
                                multiline
                                fullWidth
                                minRows={5}
                                value={beforeEditText}
                                className={classes.textField}
                            />
                        </div>
                        <div className={styles.modalRight}>
                            <ThemeProvider theme={theme}>
                                <PuiTypography size={14} color="gray-85">
                                    {t("編集後")}
                                </PuiTypography>
                                <TextField
                                    color="secondary"
                                    onChange={InputEditCaption}
                                    multiline
                                    fullWidth
                                    minRows={5}
                                    autoComplete="off"
                                    variant="outlined"
                                    defaultValue={beforeEditText}
                                    className={classes.textField}
                                />
                            </ThemeProvider>
                        </div>
                    </div>
                    <div className={styles.captionEditModalButton}>
                        <div className={styles.cancelButton}>
                            <Button
                                variant="outlined"
                                type="submit"
                                onClick={CloseEditCaptionModal}
                                style={{
                                    color: "var(--loading-bg)",
                                    fontSize: "14px",
                                    fontFamily: "noto-sans-cjk-jp,sans-serif",
                                    border: "solid 2px #5D5D5D",
                                    width: "200px",
                                    height: "40px"
                                }}
                            >
                                {t("キャンセル")}
                            </Button>
                        </div>
                        <div className={styles.updateButton}>
                            <Button
                                variant="outlined"
                                type="submit"
                                onClick={DoneEditInputCaption}
                                disabled={!isUserInput}
                                style={
                                    isUserInput
                                        ? {
                                            color: "white",
                                            fontSize: "14px",
                                            fontFamily: "noto-sans-cjk-jp,sans-serif",
                                            backgroundColor: "var(--studio-btn-bg)",

                                            width: "200px",
                                            height: "40px"
                                        }
                                        : {
                                            color: "var(--loading-bg)",
                                            fontFamily: "noto-sans-cjk-jp,sans-serif",
                                            backgroundColor: "var(--button-bg)",
                                            width: "200px",
                                            height: "40px"
                                        }
                                }
                            >
                                {t("編集")}
                            </Button>
                        </div>
                    </div>
                    <div
                        className={styles.captionEditModalAlart}
                        style={{ visibility: isErrorInputCaption ? "visible" : "hidden" }}
                    >
                        {t("※入力してください")}
                    </div>
                </div>
            </Modal>

            {/* STT自動翻訳確認モーダル */}
            <Modal open={isOpenTransitionModal} onClose={CloseTranslateModal}>
                <div className={styles.captionEditModal}>
                    <div className={styles.modalCenter}>
                        <PuiTypography size={16} color="gray-85">
                            {t("STTを自動翻訳しますか？")}
                        </PuiTypography>
                    </div>
                    <div className={styles.captionEditModalButton}>
                        <div className={styles.cancelButton}>
                            <Button variant="outlined" type="submit" onClick={CloseTranslateModal}>
                                {t("いいえ")}
                            </Button>
                        </div>
                        <div className={styles.updateButton}>
                            <Button variant="outlined" type="submit" onClick={DoneTranslateCaption}>
                                {t("はい")}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
